import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Carousel } from "react-responsive-carousel";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  Grid,
  Pagination,
  Paper,
  Typography,
  createTheme,
  useMediaQuery,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SchoolIcon from "@mui/icons-material/School";
import StyleIcon from "@mui/icons-material/Style";
import WorkspacePremiumIcon from "@mui/icons-material/EmojiEvents";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import {
  useActiveSlidersQuery,
  useAnswersQuery,
} from "../../queries/useToeicQueries";

const learningModules = [
  {
    title: "Flashcards",
    href: "/flashcards",
    icon: StyleIcon,
    label: "Vocabulary",
    description: "Build word decks, review with spaced repetition, and keep examples close.",
    color: "#6d4aff",
    bg: "#f0ecff",
  },
  {
    title: "IELTS",
    href: "/ielts",
    icon: WorkspacePremiumIcon,
    label: "Exam prep",
    description: "Prepare writing, speaking, reading, and listening in a separate IELTS track.",
    color: "#1f9b45",
    bg: "#e8f9ed",
  },
  {
    title: "TOEIC Practice",
    href: "/test",
    icon: AssignmentIcon,
    label: "Answer sheets",
    description: "Open the current TOEIC test bank, submit answers, review attempts, and rank scores.",
    color: "#2387cc",
    bg: "#e8f6ff",
  },
];

const examAccents = [
  { bg: "var(--toeic-coral)", color: "#ffffff", soft: "rgba(233, 109, 120, 0.12)" },
  { bg: "var(--toeic-sky)", color: "#ffffff", soft: "rgba(70, 168, 232, 0.12)" },
  { bg: "var(--toeic-mint)", color: "#ffffff", soft: "rgba(79, 191, 159, 0.13)" },
  { bg: "var(--toeic-gold)", color: "var(--toeic-ink)", soft: "rgba(240, 184, 77, 0.16)" },
];

const getExamAccent = (index) => examAccents[index % examAccents.length];

function HomePage() {
  const [page, setPage] = useState(1);
  const itemsPerPage = 8;
  const { data: content = [] } = useAnswersQuery("");
  const { data: slider = [] } = useActiveSlidersQuery();

  const indexOfLastItem = page * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = content?.slice(indexOfFirstItem, indexOfLastItem);

  const handleChangePage = (event, value) => {
    setPage(value);
  };

  const theme = createTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box sx={{ width: "100%", mt: 4, mb: 6 }}>
      <Paper
        elevation={0}
        sx={{
          overflow: "hidden",
          p: isMobile ? 3 : 5,
          borderRadius: "var(--toeic-radius-lg)",
          border: "1px solid var(--toeic-line)",
          bgcolor: "rgba(255, 254, 253, 0.9)",
          boxShadow: "var(--toeic-shadow-card)",
          backdropFilter: "blur(12px)",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1.05fr 0.95fr",
          gap: 4,
          alignItems: "center",
        }}
      >
        <Box>
          <Chip
            icon={<SchoolIcon />}
            label="English learning hub"
            sx={{
              mb: 2,
              color: "var(--toeic-sky-deep)",
              bgcolor: "#e8f6ff",
              border: "1px solid #bde5fb",
              "& .MuiChip-icon": { color: "var(--toeic-sky)" },
            }}
          />
          <Typography
            component="h1"
            sx={{
              color: "var(--toeic-ink)",
              fontWeight: 800,
              fontSize: { xs: 38, md: 60 },
              lineHeight: 1,
              letterSpacing: 0,
            }}
          >
            Lingualica
          </Typography>
          <Typography
            sx={{
              mt: 2,
              color: "var(--toeic-ink-soft)",
              maxWidth: 580,
              fontSize: 18,
              lineHeight: 1.65,
              fontWeight: 600,
            }}
          >
            A calmer place to study English: vocabulary, exam tracks, resources,
            and practice history in one account. TOEIC is one module, not the
            whole identity.
          </Typography>
          <Box sx={{ mt: 3, display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Button
              component={Link}
              to="/flashcards"
              variant="contained"
              startIcon={<StyleIcon />}
              sx={{
                bgcolor: "#6d4aff",
                color: "#ffffff",
                fontWeight: 700,
                px: 2.5,
                boxShadow: "var(--toeic-shadow-stamp)",
                "&:hover": { bgcolor: "#5635d6" },
              }}
            >
              Open Flashcards
            </Button>
            <Button
              component={Link}
              to="/test"
              variant="outlined"
              startIcon={<PlayArrowIcon />}
              sx={{
                borderColor: "#a9dff8",
                color: "var(--toeic-sky-deep)",
                bgcolor: "rgba(255,255,255,.5)",
                fontWeight: 700,
                px: 2.5,
                "&:hover": { borderColor: "var(--toeic-sky)", bgcolor: "#e8f6ff" },
              }}
            >
              TOEIC Practice
            </Button>
          </Box>
        </Box>

        <Paper
          elevation={0}
          sx={{
            overflow: "hidden",
            borderRadius: "22px",
            border: "1px solid var(--toeic-line)",
            bgcolor: "var(--toeic-paper)",
            boxShadow: "var(--toeic-shadow-stamp)",
          }}
        >
          {slider?.length > 0 ? (
            <Carousel
              infiniteLoop
              autoPlay
              interval={3000}
              showThumbs={false}
              showIndicators
              showStatus={false}
              emulateTouch
              swipeable
            >
              {slider.map((item) => (
                <Box
                  key={item._id || item.slider_image}
                  sx={{
                    position: "relative",
                    height: { xs: 220, sm: 300, md: 360 },
                    backgroundImage: `url(${item.slider_image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  <Typography
                    sx={{
                      position: "absolute",
                      left: 16,
                      right: 16,
                      bottom: 16,
                      color: "var(--toeic-ink)",
                      bgcolor: "rgba(255, 254, 253, 0.9)",
                      p: 1.5,
                      borderRadius: "16px",
                      fontWeight: 600,
                      textAlign: "left",
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    {item.slider_content}
                  </Typography>
                </Box>
              ))}
            </Carousel>
          ) : (
            <Box sx={{ p: 4, minHeight: 300, display: "grid", placeItems: "center" }}>
              <AutoStoriesIcon className="toeic-soft-bob" sx={{ fontSize: 84, color: "var(--toeic-sky)" }} />
            </Box>
          )}
        </Paper>
      </Paper>

      <Grid container spacing={2} sx={{ mt: 3 }}>
        {learningModules.map((module) => {
          const Icon = module.icon;

          return (
            <Grid item xs={12} md={4} key={module.title}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  borderRadius: "14px",
                  border: 0,
                  bgcolor: "var(--toeic-surface)",
                  boxShadow: "var(--toeic-shadow-card)",
                  overflow: "hidden",
                  transition: "transform 220ms var(--toeic-ease-fluid), box-shadow 180ms ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 24px 44px -30px rgba(19, 43, 63, 0.52)",
                  },
                }}
              >
                <CardActionArea
                  component={Link}
                  to={module.href}
                  sx={{
                    height: "100%",
                    p: 2.5,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 2 }}>
                    <Box
                      sx={{
                        width: 54,
                        height: 54,
                        borderRadius: "14px",
                        display: "grid",
                        placeItems: "center",
                        bgcolor: module.bg,
                        color: module.color,
                      }}
                    >
                      <Icon sx={{ fontSize: 32 }} />
                    </Box>
                    <Chip size="small" label={module.label} sx={{ bgcolor: module.bg, color: module.color, fontWeight: 700 }} />
                  </Box>
                  <Typography sx={{ color: "var(--toeic-ink-deep)", fontWeight: 700, fontSize: 21 }}>
                    {module.title}
                  </Typography>
                  <Typography sx={{ mt: 1, color: "var(--toeic-ink-soft)", lineHeight: 1.6, fontWeight: 400 }}>
                    {module.description}
                  </Typography>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Box sx={{ mt: 5, mb: 2, display: "flex", alignItems: "end", justifyContent: "space-between", gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ color: "var(--toeic-ink)", fontWeight: 700 }}>
            TOEIC Practice Sets
          </Typography>
          <Typography sx={{ color: "var(--toeic-ink-soft)" }}>
            The existing answer sheet flow lives here as one study track.
          </Typography>
        </Box>
        <Button component={Link} to="/test" sx={{ color: "var(--toeic-sky-deep)", fontWeight: 700 }}>
          Browse TOEIC
        </Button>
      </Box>

      <Grid container spacing={2}>
        {currentItems?.length > 0 ? (
          currentItems.map((item, index) => {
            const order = indexOfFirstItem + index + 1;
            const accent = getExamAccent(order - 1);

            return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={item._id}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  borderRadius: "var(--toeic-radius-md)",
                  border: "1px solid rgba(215, 234, 245, 0.72)",
                  bgcolor: "var(--toeic-surface)",
                  boxShadow: "var(--toeic-shadow-card)",
                  transition: "transform 0.22s var(--toeic-ease-fluid), box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 24px 44px -30px rgba(19, 43, 63, 0.52)",
                  },
                }}
              >
                <CardActionArea
                  component={Link}
                  to={`/answersheet/${item._id}`}
                  sx={{
                    height: "100%",
                    p: 2.5,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    gap: 2,
                    "&:hover .exam-card-arrow": {
                      color: accent.bg,
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "14px",
                        display: "grid",
                        placeItems: "center",
                        bgcolor: accent.bg,
                        color: accent.color,
                        fontSize: 22,
                        fontWeight: 900,
                        lineHeight: 1,
                      }}
                    >
                      {String(order).padStart(2, "0")}
                    </Box>
                    <Chip
                      label="Free"
                      size="small"
                      sx={{ bgcolor: accent.soft, color: accent.bg, fontWeight: 700 }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      component="h3"
                      sx={{
                        color: "var(--toeic-ink)",
                        fontSize: 18,
                        fontWeight: 900,
                        lineHeight: 1.25,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {item.answer_name}
                    </Typography>
                    <Typography sx={{ mt: 0.75, color: "var(--toeic-ink-soft)", fontSize: 13, fontWeight: 600 }}>
                      200 questions - listening + reading
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      mt: "auto",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Typography sx={{ color: "var(--toeic-ink)", fontSize: 14, fontWeight: 700 }}>
                      Start practice
                    </Typography>
                    <ArrowForwardIcon
                      className="exam-card-arrow"
                      sx={{
                        color: "var(--toeic-ink-soft)",
                        fontSize: 18,
                        flex: "0 0 auto",
                        transition: "transform 180ms ease, color 180ms ease",
                      }}
                    />
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>
            );
          })
        ) : (
          <Grid item xs={12}>
            <Typography sx={{ color: "var(--toeic-ink-soft)", fontWeight: 400 }}>
              No TOEIC practice set available
            </Typography>
          </Grid>
        )}
      </Grid>

      <Pagination
        count={Math.ceil(content?.length / itemsPerPage)}
        page={page}
        onChange={handleChangePage}
        sx={{ mt: 3, display: "flex", justifyContent: "center" }}
      />
    </Box>
  );
}

export default HomePage;
